import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import storyService from '../services/storyService';

/**
 * `?reel=<userId>` — which Portal the story viewer is showing, on any page that has a rail.
 *
 * The viewer needs the reels around the one it opens on (swiping goes to the next person), and
 * that list used to live only in memory from the tap that opened it. A reload, a shared link or
 * coming back to the page left `?reel=` in the URL with nothing to show — the viewer never
 * appeared and the URL said it had. Now the list comes from the tap, else from what the page's
 * rail already loaded, else that one person's reel is fetched; if they have nothing up, the
 * parameter is dropped rather than left lying.
 *
 * Closing steps Back only when this page pushed the entry. After a reload or a deep link there
 * is nothing of ours to go back to, so the parameter is removed in place instead.
 *
 *   railReels  what the page's StoriesRail has loaded (its `onLoaded`), if it has one
 *   location   { lat, lng } for the single-reel fetch, so distance-gated Portals resolve
 */
export default function useReelParam({ railReels = [], location = null } = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openReelUserId = searchParams.get('reel');
  const [tapped, setTapped] = useState([]);
  const [fetched, setFetched] = useState(null);
  const [doorRect, setDoorRect] = useState(null);
  const pushed = useRef(false);
  // Bumped on every tap. The page keys the viewer on it, so tapping a ring ALWAYS opens a
  // fresh viewer — even when the parameter already says that person and nothing else about
  // the props changed (a close the motion never handed back used to leave exactly that, and
  // the ring went dead until a reload).
  const [openNonce, setOpenNonce] = useState(0);

  const openReel = useCallback((userId, reels, ringRect) => {
    setTapped(reels || []);
    setDoorRect(ringRect || null);
    setOpenNonce((n) => n + 1);
    pushed.current = true;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('reel', userId); return next; });
  }, [setSearchParams]);

  const dropParam = useCallback(() => {
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('reel'); return next; }, { replace: true });
  }, [setSearchParams]);

  const closeReel = useCallback(() => {
    if (pushed.current) {
      pushed.current = false;
      navigate(-1);
      // Back can be refused (the entry was replaced, or there is nothing of ours to pop):
      // the parameter would stay with no viewer on screen, and the ring would be dead.
      // A tap in the meantime sets `pushed` again and this leaves it alone.
      setTimeout(() => {
        if (pushed.current) return;
        if (new URLSearchParams(window.location.search).get('reel')) dropParam();
      }, 600);
      return;
    }
    dropParam();
  }, [navigate, dropParam]);

  const has = (list) => !!openReelUserId && list.some((r) => r.authorUserId === openReelUserId);
  const reels = useMemo(() => {
    if (!openReelUserId) return [];
    // The rail's current list first: it's refreshed when your own Portal goes up, and an
    // earlier tap's copy may still have it words-only.
    if (has(railReels)) return railReels;
    if (has(tapped)) return tapped;
    if (fetched?.authorUserId === openReelUserId) return [fetched];
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReelUserId, tapped, railReels, fetched]);

  // Nothing in memory for this person: ask for their reel on its own.
  const inMemory = has(tapped) || has(railReels);
  useEffect(() => {
    if (!openReelUserId || inMemory || fetched?.authorUserId === openReelUserId) return undefined;
    let cancelled = false;
    storyService.getReel(openReelUserId, { lat: location?.lat ?? null, lng: location?.lng ?? null })
      .then((r) => {
        if (cancelled) return;
        if (r?.success && r.data) setFetched(r.data);
        else dropParam();                                // nothing up: don't leave the URL lying
      })
      .catch(() => { if (!cancelled) dropParam(); });
    return () => { cancelled = true; };
  }, [openReelUserId, inMemory, fetched?.authorUserId, location?.lat, location?.lng, dropParam]);

  return { openReelUserId, reels, doorRect, openReel, closeReel, openNonce };
}
